import { useCallback, useEffect, useState } from 'react';
import { useTransientStatus } from '../../../shared/hooks/useTransientStatus.js';
import { loadPersonalNikkeData, syncPersonalNikkeData } from '../api/personalNikkeApi.js';

const EMPTY_DATA = {
  profile: null,
  characters: [],
  lookup: null,
  catalog: null,
  source: '',
  warnings: [],
};

export function usePersonalNikke({ enabled = true } = {}) {
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const { message: notice, show: showNotice, clear: clearNotice } = useTransientStatus();

  const refresh = useCallback(async ({ silent = false, forceRefresh = false } = {}) => {
    if (!enabled) return EMPTY_DATA;
    if (!silent) setLoading(true);
    try {
      const next = await loadPersonalNikkeData({ forceRefresh });
      setData(next);
      setError('');
      return next;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '내 니케 정보를 불러오지 못했습니다.');
      return EMPTY_DATA;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setLoading(true);
    loadPersonalNikkeData()
      .then((next) => { if (active) { setData(next); setError(''); } })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : '내 니케 정보를 불러오지 못했습니다.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [enabled]);

  const sync = useCallback(async () => {
    if (syncing) return false;
    setSyncing(true);
    setError('');
    clearNotice();
    try {
      const result = await syncPersonalNikkeData();
      await refresh({ silent: true, forceRefresh: true });
      showNotice({
        type: 'success',
        message: `${Number(result.ownedCount || 0).toLocaleString('ko-KR')}명의 니케 정보를 동기화했습니다.`,
      });
      window.dispatchEvent(new CustomEvent('dororaid:personal-nikke-changed'));
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '내 니케 동기화에 실패했습니다.');
      return false;
    } finally {
      setSyncing(false);
    }
  }, [clearNotice, refresh, showNotice, syncing]);

  return { ...data, loading, syncing, error, notice, refresh, sync };
}
